import { z } from 'zod';

import { type SelfDisclosureField, getFieldByKey } from './fields';

/**
 * Config schema v2 = Source of Truth for a generated self-disclosure form
 * (Sanierungs-Spec §2.3, Produkt-Pivot E3). The SAME schema instance
 * validates: generator UI, `parkToolTemplateAction` (+ 16 KB serialized-config
 * cap), `saveToolTemplateToWorkspacesAction`, the claim re-validation, the
 * settings editor, and the render entry point.
 *
 * v2 axes: `language` (de / en / bilingual) + `personCount` (1–3) + free field
 * selection from the advice catalog. `dskPhase` is GONE – there is no
 * inadmissible field combination anymore (yellow is selectable; red
 * categories structurally do not exist as fields).
 *
 * Version bridge: the v1 schema stays as a frozen copy; `upgradeConfigV1` is
 * a total, deterministic v1→v2 lift; `parseAnySelfDisclosureConfig` is the
 * single parse entry for ALL read boundaries (version dispatch incl. the
 * defensive NULL fallback on `raw.version` – `workspace_templates
 * .config_version` is nullable).
 */

/** Current supported config version. Bumped on breaking config changes. */
export const SELF_DISCLOSURE_CONFIG_VERSION = 2 as const;

export const SelfDisclosureSenderSchema = z.object({
  displayName: z
    .string()
    .min(1, 'Bitte gib den Namen des Vermieters oder der Firma an.')
    .max(200), // Vermieter-Name ODER Firma
  isCompany: z.boolean().default(false),
  contactPerson: z.string().max(200).optional(), // bei Firma
  street: z.string().max(200).optional(),
  postalCode: z.string().max(16).optional(),
  city: z.string().max(120).optional(),
  email: z
    .string()
    .email('Bitte gib eine gültige E-Mail-Adresse an.')
    .max(254)
    .optional(),
  dpoContact: z.string().max(300).optional(), // DSB nur falls angegeben
  propertyLabel: z.string().max(300).optional(), // „Wohnung Musterstraße 1, 2. OG"
});

export const SelfDisclosureFieldOptionsSchema = z.object({
  incomeVariant: z.enum(['amount', 'threshold']).optional(),
  incomeThresholdEur: z.number().int().positive().max(1_000_000).optional(),
  isSocialHousing: z.boolean().default(false),
  publicPayerDirect: z.boolean().default(false),
});

type FieldOptionsShape = z.infer<typeof SelfDisclosureFieldOptionsSchema>;

/**
 * Shared field-level checks (v2 – the v1 phase-order check is gone since
 * there is no phase axis anymore; everything else carries over 1:1).
 */
function refineEnabledFields(
  config: { enabledFields: string[]; fieldOptions: FieldOptionsShape },
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();

  config.enabledFields.forEach((key, index) => {
    const field: SelfDisclosureField | undefined = getFieldByKey(key);

    if (!field) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['enabledFields', index],
        message: `Unbekanntes Feld: ${key}`,
      });

      return;
    }

    if (seen.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['enabledFields', index],
        message: `Doppeltes Feld: ${key}`,
      });
    }

    seen.add(key);

    if (field.socialHousingOnly && !config.fieldOptions.isSocialHousing) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['enabledFields', index],
        message: `Feld "${key}" ist nur für öffentlich geförderte Wohnungen zulässig (isSocialHousing).`,
      });
    }

    // Income section auto-deactivates when the public payer settles the rent
    // directly.
    if (field.incomeVariant && config.fieldOptions.publicPayerDirect) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['enabledFields', index],
        message: `Einkommensfelder entfallen, wenn die Miete vollständig direkt von einer öffentlichen Stelle gezahlt wird.`,
      });
    }

    // The threshold-confirmation variant needs a concrete threshold amount.
    if (
      field.incomeVariant === 'threshold' &&
      config.fieldOptions.incomeThresholdEur === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['fieldOptions', 'incomeThresholdEur'],
        message:
          'Für die Betragsgrenzen-Bestätigung musst du im Schritt „Felder“ eine Betragsgrenze in Euro angeben.',
      });
    }
  });
}

/**
 * Shared non-sender config shape (Spec 2026-07-08 §3.1). Extracted so the loose
 * READ/RENDER boundary and the strict WRITE/UI (Author) boundary derive from ONE
 * shape source – no drift. `SelfDisclosureConfigSchema` is a `ZodEffects` (it
 * carries `.superRefine(refineEnabledFields)`) and therefore has no `.extend`;
 * pulling the shape out as an object literal lets both config schemas compose it.
 */
const SelfDisclosureConfigShape = {
  version: z.literal(SELF_DISCLOSURE_CONFIG_VERSION),
  templateType: z.literal('self_disclosure'),
  /** Form language: German, English, or bilingual (OQ4: in V1, killswitch =
   *  reducing this enum – a one-line Zod change). */
  language: z.enum(['de', 'en', 'de-en']),
  /** Number of persons filling in the form (answer columns in the PDF). */
  personCount: z.number().int().min(1).max(3),
  /**
   * Optional extra answer/signature column for a guarantor (§2). Additive with
   * `.default(false)`: legacy configs without the key parse unchanged, and every
   * guarantor render path is gated on it, so an old config renders byte-identical
   * to today (the Subject fingerprint emits the `guarantor` key only when true).
   */
  includeGuarantor: z.boolean().default(false),
  // Cap raised 40 → 60: the catalog grew with the pivot; still an
  // anon-surface guard only (DB bloat / DoS).
  enabledFields: z.array(z.string().max(64)).min(1).max(60),
  fieldOptions: SelfDisclosureFieldOptionsSchema,
} as const;

/**
 * LOOSE config schema – READ/RENDER boundary (Spec 2026-07-08 §3.1). Address
 * fields stay OPTIONAL: this is what `render-pdf.ts` (`layoutDocumentDetailed`
 * / `renderSelfDisclosurePdf`) parses, and what `parseAnySelfDisclosureConfig`
 * / `upgradeConfigV1` produce. Behaviour is UNCHANGED from before the Author
 * split – an addressless legacy template still renders / downloads / sends.
 */
export const SelfDisclosureConfigSchema = z
  .object({ ...SelfDisclosureConfigShape, sender: SelfDisclosureSenderSchema })
  .superRefine(refineEnabledFields);

export type SelfDisclosureConfig = z.infer<typeof SelfDisclosureConfigSchema>;
export type SelfDisclosureSender = z.infer<typeof SelfDisclosureSenderSchema>;
export type SelfDisclosureFieldOptions = z.infer<
  typeof SelfDisclosureFieldOptionsSchema
>;

// ── Author (strict) schemas – WRITE/UI boundary (Spec 2026-07-08 §3.1) ────────

/**
 * Strict Author sender: the Art. 13 (1) (a) address (Straße/PLZ/Ort) is
 * MANDATORY. E-Mail stays optional/recommended (Spec §3.1/OQ-B3 – a later
 * Legal upgrade to `required` is safe here because this is a WRITE-only schema
 * and never touches the read boundary). Extends the loose sender, so an Author
 * value is structurally assignable to `SelfDisclosureSender` (address only
 * narrows optional → required).
 */
export const SelfDisclosureSenderAuthorSchema =
  SelfDisclosureSenderSchema.extend({
    // `required_error`/`invalid_type_error` mirror the `.min(1)` message so the
    // SAME string surfaces whether the field is missing (undefined – the UI
    // trims empty inputs to undefined via `draftToCandidate`) OR an empty
    // string. `firstErrorField`/`CONTEXTUAL_MESSAGES` rely on that exact text.
    street: z
      .string({
        required_error: 'Bitte gib die Straße und Hausnummer an.',
        invalid_type_error: 'Bitte gib die Straße und Hausnummer an.',
      })
      .min(1, 'Bitte gib die Straße und Hausnummer an.')
      .max(200),
    postalCode: z
      .string({
        required_error: 'Bitte gib die Postleitzahl an.',
        invalid_type_error: 'Bitte gib die Postleitzahl an.',
      })
      .min(1, 'Bitte gib die Postleitzahl an.')
      .max(16),
    city: z
      .string({
        required_error: 'Bitte gib den Ort an.',
        invalid_type_error: 'Bitte gib den Ort an.',
      })
      .min(1, 'Bitte gib den Ort an.')
      .max(120),
  });

/**
 * Strict Author config – SAME shape, strict sender. ALL write-/UI-boundaries
 * (Create/Update actions, Park/SaveToWorkspaces tool actions, `deriveState`)
 * validate against THIS. An Author config is structurally assignable to the
 * loose `SelfDisclosureConfig` (Author ⊆ loose), so it feeds `render*` and
 * `DerivedState.config` without a cast.
 */
export const SelfDisclosureConfigAuthorSchema = z
  .object({
    ...SelfDisclosureConfigShape,
    sender: SelfDisclosureSenderAuthorSchema,
  })
  .superRefine(refineEnabledFields);

export type SelfDisclosureSenderAuthor = z.infer<
  typeof SelfDisclosureSenderAuthorSchema
>;
export type SelfDisclosureConfigAuthor = z.infer<
  typeof SelfDisclosureConfigAuthorSchema
>;

// ── Version bridge (Spec §2.3 – backwards compatibility) ──────────────────────

/**
 * FROZEN v1 schema – structural validation of stored v1 configs only.
 *
 * Deliberate difference to the historic live v1 schema: the phase-binding
 * superRefine is NOT reproduced here (it needed the removed `dskPhase` axis
 * on the catalog). Historic rows were validated with the strict schema at
 * write time; `upgradeConfigV1` re-validates everything that still matters
 * through the v2 schema (field existence, socialHousing binding, income
 * rules), so the bridge stays total AND safe.
 */
export const SelfDisclosureConfigV1Schema = z.object({
  version: z.literal(1),
  templateType: z.literal('self_disclosure'),
  dskPhase: z.enum(['A', 'B', 'C']),
  enabledFields: z.array(z.string().max(64)).min(1).max(40),
  fieldOptions: SelfDisclosureFieldOptionsSchema,
  sender: SelfDisclosureSenderSchema,
});

export type SelfDisclosureConfigV1 = z.infer<
  typeof SelfDisclosureConfigV1Schema
>;

/**
 * Total, deterministic v1→v2 lift. Every valid v1 config yields a valid v2
 * config: `enabledFields` carries over unchanged (the v2 field keys are a
 * SUPERSET of the 23 v1 keys – invariant fixed by unit test), the dropped
 * phase axis maps to the v2 defaults `language: 'de'`, `personCount: 1`.
 */
export function upgradeConfigV1(
  v1: SelfDisclosureConfigV1,
): SelfDisclosureConfig {
  const { dskPhase: _dskPhase, ...rest } = v1;

  return SelfDisclosureConfigSchema.parse({
    ...rest,
    version: SELF_DISCLOSURE_CONFIG_VERSION,
    language: 'de',
    personCount: 1,
  });
}

export type ParseAnySelfDisclosureConfigResult =
  | { ok: true; config: SelfDisclosureConfig; upgraded: boolean }
  | { ok: false };

const VersionPeekSchema = z.object({ version: z.number() });

/**
 * Single parse entry for ALL read boundaries (Spec §2.3, R1).
 *
 * Version dispatch: 2 → v2 parse; 1 → v1 parse + upgrade; NULL → defensive
 * fallback on `raw.version` (`workspace_templates.config_version` is
 * nullable; all known write paths set it, so the fallback is purely
 * defensive); everything else → `{ ok: false }`. Never throws.
 */
export function parseAnySelfDisclosureConfig(
  raw: unknown,
  version: number | null,
): ParseAnySelfDisclosureConfigResult {
  let effectiveVersion = version;

  if (effectiveVersion === null) {
    const peeked = VersionPeekSchema.safeParse(raw);

    if (!peeked.success) return { ok: false };

    effectiveVersion = peeked.data.version;
  }

  if (effectiveVersion === SELF_DISCLOSURE_CONFIG_VERSION) {
    const parsed = SelfDisclosureConfigSchema.safeParse(raw);

    return parsed.success
      ? { ok: true, config: parsed.data, upgraded: false }
      : { ok: false };
  }

  if (effectiveVersion === 1) {
    const parsedV1 = SelfDisclosureConfigV1Schema.safeParse(raw);

    if (!parsedV1.success) return { ok: false };

    const { dskPhase: _dskPhase, ...rest } = parsedV1.data;
    const lifted = SelfDisclosureConfigSchema.safeParse({
      ...rest,
      version: SELF_DISCLOSURE_CONFIG_VERSION,
      language: 'de',
      personCount: 1,
    });

    return lifted.success
      ? { ok: true, config: lifted.data, upgraded: true }
      : { ok: false };
  }

  return { ok: false };
}
